import React, { useContext, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "./location";

export const LOGIN_REDIRECT_PARAM = "login_redirect";
export const ACCESS_TOKEN_COOKIE_NAME = "aqora-access-token";

export interface TokenClaims {
  sub: string;
  sss: string;
  typ: "refresh" | "access";
  iat: number;
  exp: number;
}

export const jwtClaims = (token: string): TokenClaims => jwtDecode(token);

export interface AuthContextValue {
  getToken: () => string | undefined;
  setTokens: (tokens: { access?: string; refresh?: string }) => void;
  revokeToken: (token_type: "access" | "refresh") => void;
  revokeTokens: () => void;
  isAuthenticated: boolean;
  userId?: string | undefined;
}

export const AuthContext = React.createContext<AuthContextValue>({
  getToken: () => undefined,
  setTokens: () => {},
  revokeToken: () => {},
  revokeTokens: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

/**
 * Login page URL that brings the user back to the current page, query included.
 * The hash is left out: browsers never send it to the server, so including it
 * would make the server-rendered link differ from the hydrated one.
 */
export const useLoginRedirect = () => {
  const { pathname, search } = useLocation();
  return getLoginRedirect(`${pathname}${search}`);
};

export const getLoginRedirect = (target: string) =>
  `/login?${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(target)}`;

/**
 * Resolves a post-login redirect to a path on this site. Anything pointing
 * at another origin (`https://evil.example`, `//evil.example`,
 * `javascript:...`) falls back to the home page.
 */
export const sameOriginPath = (target: string, origin: string): string => {
  try {
    const url = new URL(target, origin);
    const path = `${url.pathname}${url.search}${url.hash}`;
    // `https://aqora.io//evil.example` is on this origin, but its path on its
    // own (`//evil.example`) reads as another host, so check the path too.
    if (url.origin !== origin || new URL(path, origin).origin !== origin) {
      return "/";
    }
    return path;
  } catch {
    return "/";
  }
};

export const useOnAuthCallback = () => {
  const location = useLocation();
  return useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has(LOGIN_REDIRECT_PARAM)) {
      const redirect = searchParams.get(LOGIN_REDIRECT_PARAM)!;
      location.replace(sameOriginPath(redirect, location.origin));
    } else if (searchParams.has("client_id")) {
      location.replace(`/oauth2/authorize?${searchParams.toString()}`);
    } else {
      location.replace("/");
    }
  }, [location]);
};
